import {
  logEvent,
  setAnalyticsCollectionEnabled,
  setConsent,
  setDefaultEventParameters,
  setUserId,
} from "firebase/analytics";
import { getAnalyticsInstance } from "../firebase/config";

const APP_NAME = "peak_signup";

class AnalyticsService {
  private isEnabled = false;
  private userId: string | null = null;
  private lastScreen: string | null = null;
  private pendingCollectionSyncTimeout: ReturnType<typeof setTimeout> | null = null;

  private isAvailable(): boolean {
    return this.isEnabled && getAnalyticsInstance() !== null;
  }

  setConsent(enabled: boolean) {
    this.isEnabled = enabled;
    if (!enabled) this.lastScreen = null;
    try {
      setConsent({
        analytics_storage: enabled ? "granted" : "denied",
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
      });
    } catch {
      // Analytics was never initialised.
    }
    this.syncCollectionState();
    this.applyUser();
  }

  private syncCollectionState(retryCount = 0) {
    const analytics = getAnalyticsInstance();
    if (analytics) {
      try {
        setAnalyticsCollectionEnabled(analytics, this.isEnabled);
        if (this.isEnabled) {
          setDefaultEventParameters({ app_name: APP_NAME });
        }
      } catch (error) {
        console.warn("Analytics collection toggle failed:", error);
      }
      return;
    }

    if (retryCount < 5) {
      if (this.pendingCollectionSyncTimeout) clearTimeout(this.pendingCollectionSyncTimeout);
      this.pendingCollectionSyncTimeout = setTimeout(() => {
        this.syncCollectionState(retryCount + 1);
      }, 500);
    }
  }

  setUser(userId: string | null) {
    this.userId = userId;
    this.applyUser();
  }

  private applyUser() {
    const analytics = getAnalyticsInstance();
    if (!this.isAvailable() || !analytics) return;
    try {
      setUserId(analytics, this.isEnabled ? this.userId : null);
    } catch (error) {
      console.error("Analytics setUser error:", error);
    }
  }

  trackScreen(screenName: string, pagePath: string) {
    const analytics = getAnalyticsInstance();
    if (!this.isAvailable() || !analytics) return;
    const key = `${screenName}|${pagePath}`;
    if (key === this.lastScreen) return;
    this.lastScreen = key;

    const page_location =
      typeof window !== "undefined" ? `${window.location.origin}${pagePath}` : pagePath;

    try {
      setDefaultEventParameters({ app_name: APP_NAME, page_location, page_path: pagePath });
      logEvent(analytics, "screen_view", {
        firebase_screen: screenName,
        firebase_screen_class: screenName,
        app_name: APP_NAME,
        page_path: pagePath,
        page_title: screenName,
        page_location,
      });
    } catch (error) {
      console.error("Analytics trackScreen error:", error);
    }
  }

  trackAuth(action: "login" | "signup" | "logout", method?: string) {
    const eventName = action === "signup" ? "sign_up" : action;
    this.event(eventName, { method: method || "email" });
  }

  trackProfileComplete(userId: string) {
    this.event("complete_profile", { user_id: userId });
  }

  trackOnboarding(step: string, completed = false) {
    this.event("onboarding_step", { step_name: step, completed });
  }

  trackCampaign(campaignCode: string) {
    this.event("campaign_click", { campaign_code: campaignCode });
  }

  private event(name: string, params?: Record<string, string | number | boolean>) {
    const analytics = getAnalyticsInstance();
    if (!this.isAvailable() || !analytics) return;
    try {
      logEvent(analytics, name, { app_name: APP_NAME, ...params });
    } catch (error) {
      console.warn("Analytics event failed:", error);
    }
  }
}

export const analyticsService = new AnalyticsService();
